import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useNavigate, useParams } from 'react-router-dom';
import { TableLink } from '@/components/global';
import { LocationLink } from '@/components/locations';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { ContentPageHeader, MetricGrid, SummaryMetricCard } from '@/components/global';
import { WarehouseAssortmentPanel, WarehouseTypeBadge } from '@/components/locations';
import { DaysOfCoverBadge, StockStatusBadge } from '@/components/inventory';
import { ProductIdentity } from '@/components/products';
import { RAGBadge } from '@/components/global/RAGBadge';
import { Barcode, Store, Package, Truck, PoundSterling, Pencil } from 'lucide-react';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: 'HUB' | 'SPOKE';
  parentWarehouseId: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  postcode: string | null;
  country: string;
  monthlyOperatingCost: number;
  isActive: boolean;
  childWarehouses?: Warehouse[];
}

interface StockLevel {
  id: string;
  productId: string;
  quantityOnHand: number;
  quantityReserved: number;
  minLevel: number | null;
  maxLevel: number | null;
  daysOfCover: number;
  ragStatus: 'green' | 'amber' | 'red';
  product?: {
    id: string;
    sku: string;
    name: string;
    imageUrl?: string | null;
    media?: Array<{ url: string; mediaType: string }>;
  };
}

interface Transfer {
  id: string;
  transferNumber: string;
  transferDate: string;
  status: string;
  totalQuantity: number;
  toWarehouse?: {
    name: string;
  };
}

export default function HubDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [stockLevels, setStockLevels] = useState<StockLevel[]>([]);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [warehouseRes, stockRes, transfersRes] = await Promise.all([
        apiFetch(`/api/v1/warehouses/${id}?includeChildren=true`),
        apiFetch(`/api/v1/warehouses/${id}/stock`),
        apiFetch(`/api/v1/transfers?fromWarehouseId=${id}`),
      ]);

      if (warehouseRes.ok) {
        const data = await warehouseRes.json();
        setWarehouse(data);
      }

      if (stockRes.ok) {
        const data = await stockRes.json();
        setStockLevels(data);
      }

      if (transfersRes.ok) {
        const data = await transfersRes.json();
        setTransfers(data.items || []);
      }
    } catch (error) {
      console.error('Error loading hub data:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <div className="space-y-6">
          <div className="animate-pulse">Loading...</div>
        </div>
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div className="min-h-screen bg-muted/30">
        <div className="space-y-6">
          <div className="text-center py-8 text-muted-foreground">Hub not found</div>
        </div>
      </div>
    );
  }

  const uniqueSkus = new Set(
    stockLevels.filter((sl) => sl.quantityOnHand > 0).map((sl) => sl.productId)
  ).size;
  const spokesCount = warehouse.childWarehouses?.length ?? 0;
  const totalStockValue = stockLevels.reduce((sum, sl) => sum + sl.quantityOnHand, 0);
  const inTransitTransfers = transfers.filter((t) => t.status === 'SHIPPED');
  const inTransitUnits = inTransitTransfers.reduce((sum, t) => sum + (t.totalQuantity ?? 0), 0);

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <ContentPageHeader
          back={{ onClick: () => navigate('/locations') }}
          title={warehouse.name}
          badge={<WarehouseTypeBadge type="HUB" />}
          description={[warehouse.city, warehouse.postcode].filter(Boolean).join(', ')}
          actions={
            <Button
              variant="outline"
              onClick={() => navigate(`/locations/hubs/${id}/edit`)}
              className="w-full sm:w-auto"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
          }
        />

        <MetricGrid className="mb-6 lg:grid-cols-5">
          <SummaryMetricCard
            label="Unique SKUs"
            value={uniqueSkus}
            icon={Barcode}
          />
          <SummaryMetricCard
            label="Spokes"
            value={spokesCount}
            icon={Store}
          />
          <SummaryMetricCard
            label="Total Stock Units"
            value={totalStockValue.toLocaleString()}
            icon={Package}
          />
          <SummaryMetricCard
            label="Pending Transfers"
            value={transfers.filter((t) => t.status !== 'RECEIVED' && t.status !== 'CANCELLED').length}
            icon={Truck}
          />
          <SummaryMetricCard
            label="Monthly Operating Cost"
            value={`£${Number(warehouse.monthlyOperatingCost).toLocaleString()}`}
            icon={PoundSterling}
          />
        </MetricGrid>

        {inTransitTransfers.length > 0 && (
          <Card className="mb-6 border-yellow-200 bg-yellow-50/50 dark:border-yellow-900 dark:bg-yellow-950/20">
            <div className="p-4">
              <div className="flex items-center justify-between gap-4 mb-3">
                <div>
                  <h2 className="font-semibold">In transit to spokes</h2>
                  <p className="text-sm text-muted-foreground">
                    {inTransitUnits.toLocaleString()} unit(s) reserved across{' '}
                    {inTransitTransfers.length} shipment(s)
                  </p>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transfer</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead className="text-right">Units</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inTransitTransfers.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>
                        <TableLink to={`/locations/transfers/${t.id}`}>
                          {t.transferNumber}
                        </TableLink>
                      </TableCell>
                      <TableCell>{t.toWarehouse?.name ?? '—'}</TableCell>
                      <TableCell className="text-right">{t.totalQuantity ?? 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}

        <Tabs defaultValue="spokes" className="space-y-4">
          <TabsList>
            <TabsTrigger value="spokes">Spokes</TabsTrigger>
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="stock">Stock Levels</TabsTrigger>
            <TabsTrigger value="transfers">Transfers</TabsTrigger>
          </TabsList>

          <TabsContent value="spokes">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Spoke</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {warehouse.childWarehouses?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                        No spokes connected to this hub
                      </TableCell>
                    </TableRow>
                  ) : (
                    warehouse.childWarehouses?.map((spoke) => (
                      <TableRow key={spoke.id}>
                        <TableCell>
                          <LocationLink
                            locationType="SPOKE"
                            locationId={spoke.id}
                            name={spoke.name}
                          />
                        </TableCell>
                        <TableCell>{spoke.city || '-'}</TableCell>
                        <TableCell>
                          <RAGBadge status={spoke.isActive ? 'green' : 'red'} label={spoke.isActive ? 'Active' : 'Inactive'} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="products">
            {id ? <WarehouseAssortmentPanel warehouseId={id} /> : null}
          </TabsContent>

          <TabsContent value="stock">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">On Hand</TableHead>
                    <TableHead className="hidden text-right md:table-cell">Reserved</TableHead>
                    <TableHead className="hidden sm:table-cell">Days Cover</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stockLevels.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No stock in this hub
                      </TableCell>
                    </TableRow>
                  ) : (
                    stockLevels.map((sl) => (
                      <TableRow key={sl.id}>
                        <TableCell>
                          <ProductIdentity
                            productId={sl.productId}
                            name={sl.product?.name ?? '—'}
                            sku={sl.product?.sku}
                            showSku={false}
                            size="sm"
                            product={{
                              imageUrl: sl.product?.imageUrl ?? null,
                              media: sl.product?.media ?? undefined,
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{sl.product?.sku || '-'}</TableCell>
                        <TableCell className="text-right">{sl.quantityOnHand}</TableCell>
                        <TableCell className="hidden text-right md:table-cell">{sl.quantityReserved}</TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <DaysOfCoverBadge days={sl.daysOfCover} />
                        </TableCell>
                        <TableCell>
                          <StockStatusBadge
                            quantityOnHand={sl.quantityOnHand}
                            minLevel={sl.minLevel}
                            maxLevel={sl.maxLevel}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="transfers">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transfer #</TableHead>
                    <TableHead className="hidden sm:table-cell">Date</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead className="hidden text-right sm:table-cell">Quantity</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transfers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No transfers from this hub
                      </TableCell>
                    </TableRow>
                  ) : (
                    transfers.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell>
                          <TableLink to={`/locations/transfers/${t.id}`}>
                            {t.transferNumber}
                          </TableLink>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">{new Date(t.transferDate).toLocaleDateString()}</TableCell>
                        <TableCell>{t.toWarehouse?.name || '-'}</TableCell>
                        <TableCell className="hidden text-right sm:table-cell">{t.totalQuantity}</TableCell>
                        <TableCell>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-muted">
                            {t.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
